import { HOMEWORK_DEFAULT_TITLE_KEYS } from '@cc/schema';
import { useCustom, useTranslate } from '@refinedev/core';
import { Button, Table, Tag, Typography } from 'antd';
import { useMemo, useState } from 'react';
import {
  EmptyState,
  ResultCard,
  ToolAssignButton,
  ToolSection,
  fmtDay,
} from '../../components/ToolSection';
import type { AssignPreset } from '../homework/model';
import {
  PROFILE_FIELDS,
  VALUES_EXERCISES,
  WHEEL_FIELDS,
  sectorLabel,
  valuesEmpty,
  type ProfileRow,
  type WheelRow,
  type WheelSector,
} from './model';
import { RateWheelDrawer } from './RateWheelDrawer';
import { ValuesProfileCard } from './ValuesProfileCard';
import { WindRoseHub } from './WindRoseHub';

/**
 * E36 · ValuesProfile (portal) + RateValuesWheel (in session) + AssignHomework (values exercise) — "Client Hub — Values": the same
 * profile the client has on the phone, READ-ONLY — their ranked values (a compact table: each with its wheel gap, questionnaire gap,
 * committed actions, activities, linked records and the chosen way), the wheel, and the wind rose (E53). The therapist rates the wheel
 * WITH the client in session (Q-e36g1-3 A, replacing Q-e36-4 A for the wheel only) from a side drawer on the wheel card: the rating
 * lands on the client's wheel marked "rated in session", is the practice's record, and completes no wheel the client was asked to do.
 * The card sort and the questionnaire stay on the phone. ONE "Assign" button (Q-portal-ux-1 A) opens the Assign homework drawer with the
 * four exercises (card sort · wheel · questionnaire · wind rose); the due date is optional.
 *
 * Design PORTAL-SECTIONS-2026-10-22 §2.4. Visible through a relation with VIEW permission; rating in session needs a granted relation
 * (the server's E34 verdict). NOTHING AUTOMATIC: no alert, score or notification is derived from a value, a rating or an answer.
 */
type Props = { clientId: string; clientName: string; locale: 'en' | 'ru' };

export const ClientValuesTab = ({ clientId, clientName, locale }: Props) => {
  const t = useTranslate() as unknown as (
    k: string,
    o?: Record<string, unknown>,
  ) => string;

  const profile = useCustom<ProfileRow>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'valuesProfile',
      fields: PROFILE_FIELDS,
      variables: {
        subject: { name: 'subject', type: 'ID', value: clientId },
        language: { name: 'language', type: 'String', value: locale },
      },
    },
    queryOptions: { enabled: Boolean(clientId), retry: false },
  });
  const wheelQuery = useCustom<WheelRow>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'valuesWheel',
      fields: WHEEL_FIELDS,
      variables: { subject: { name: 'subject', type: 'ID', value: clientId } },
    },
    queryOptions: { enabled: Boolean(clientId), retry: false },
  });

  const view = profile.data?.data;
  const wheel = (wheelQuery.data?.data ?? null) as WheelRow | null;
  const day = (iso: string) => fmtDay(iso, locale);

  const [rateOpen, setRateOpen] = useState(false);

  // ── the one Assign: four exercises, the due date optional ─────────────────────────────────
  const assignPreset: AssignPreset = useMemo(
    () => ({
      kind: 'tool',
      target: 'valuesSort',
      targets: VALUES_EXERCISES,
      defaults: Object.fromEntries(
        VALUES_EXERCISES.map((x) => [
          x,
          {
            title: t(HOMEWORK_DEFAULT_TITLE_KEYS[x]), // Q-ru-3 A: the shared table — left as it is, no title is stored
            reminderPolicy: 'default' as const,
            deadline: { shape: 'due' as const, optional: true },
          },
        ]),
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locale],
  );
  const assignButton = (primary?: boolean, label?: string) => (
    <ToolAssignButton
      clientId={clientId}
      clientName={clientName}
      preset={assignPreset}
      primary={primary}
      label={label}
      onAssigned={() => {
        profile.refetch();
        wheelQuery.refetch();
      }}
    />
  );

  const empty = valuesEmpty(view, wheel);

  const sectors = wheel?.sectors ?? [];
  const anyStatement = sectors.some((s) => s.valueStatement);
  const columns = [
    {
      title: t('values.hub.wheel.sector'),
      render: (_: unknown, r: WheelSector) => (
        <div>
          <div>{sectorLabel(locale, wheel?.preset, r.sectorId, r.name)}</div>
          {r.valueStatement ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {r.valueStatement}
            </Typography.Text>
          ) : anyStatement ? (
            <Typography.Text type="secondary">—</Typography.Text>
          ) : null}
        </div>
      ),
    },
    {
      title: t('values.hub.wheel.now'),
      width: 70,
      render: (_: unknown, r: WheelSector) => r.current ?? '—',
    },
    {
      title: t('values.hub.wheel.ideal'),
      width: 90,
      render: (_: unknown, r: WheelSector) => r.ideal ?? '—',
    },
    {
      title: t('values.hub.wheel.gap'),
      width: 130,
      render: (_: unknown, r: WheelSector) => r.gap ?? '—',
    },
    {
      title: t('values.hub.wheel.steps'),
      render: (_: unknown, r: WheelSector) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {r.obstacles.map((o) => (
            <span key={o.obstacleId}>
              {t('values.hub.wheel.obstacle', {
                obstacle: o.obstacle,
                severity: o.severity,
              })}
            </span>
          ))}
          {r.actions.map((a) => (
            <span key={a.actionId}>
              {a.action} ·{' '}
              {a.status === 'done'
                ? t('values.hub.profile.actionDone')
                : t('values.hub.profile.actionOpen')}
            </span>
          ))}
        </div>
      ),
    },
  ];

  const wheelRatedLine = wheel?.ratedAt
    ? t('values.hub.wheel.ratedAt', { date: day(wheel.ratedAt) })
    : null;

  return (
    <ToolSection
      testId="values-hub"
      title={t('values.hub.title')}
      purpose={t('values.hub.purpose')}
      actions={empty ? undefined : assignButton()}
      loadError={profile.isError ? t('values.hub.loadError') : null}
      loadErrorTestId="values-hub-load-error"
      loading={profile.isLoading || wheelQuery.isLoading}
      loadingText={t('pages.toolSection.loading')}
    >
      {empty ? (
        <EmptyState
          text={t('values.hub.empty')}
          primary={assignButton(true, t('values.hub.emptyAssign'))}
        />
      ) : (
        <>
          <ValuesProfileCard view={view} locale={locale} />

          <ResultCard
            testId="values-hub-wheel"
            role="result"
            collapsible
            summary={wheelRatedLine}
            title={t('values.hub.wheel.title')}
            actions={
              wheel ? (
                <Button
                  onClick={() => setRateOpen(true)}
                  data-testid="values-hub-rate-open"
                >
                  {t('values.hub.wheel.rate.open')}
                </Button>
              ) : null
            }
          >
            {!wheel ? (
              <Typography.Text
                type="secondary"
                data-testid="values-hub-wheel-empty"
              >
                {t('values.hub.wheel.empty')}
              </Typography.Text>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {wheel.ratedAt ? (
                  wheel.ratedInSession ? (
                    <div>
                      <Tag color="blue" data-testid="values-hub-rated-in-session">
                        {wheel.ratedByName
                          ? t('values.hub.wheel.ratedInSession', {
                              name: wheel.ratedByName,
                              date: day(wheel.ratedAt),
                            })
                          : t('values.hub.wheel.ratedInSessionNoName', {
                              date: day(wheel.ratedAt),
                            })}
                      </Tag>
                    </div>
                  ) : (
                    <Typography.Text
                      type="secondary"
                      data-testid="values-hub-rated-at"
                    >
                      {wheelRatedLine}
                    </Typography.Text>
                  )
                ) : (
                  <Typography.Text type="secondary">
                    {t('values.hub.wheel.notRated')}
                  </Typography.Text>
                )}
                <Table<WheelSector>
                  columns={columns}
                  dataSource={sectors}
                  onRow={(r) =>
                    ({ 'data-testid': `values-hub-sector-${r.sectorId}` } as never)
                  }
                  pagination={false}
                  rowKey="sectorId"
                  scroll={{ x: 'max-content' }}
                  size="small"
                />
              </div>
            )}
          </ResultCard>
        </>
      )}

      <WindRoseHub
        clientId={clientId}
        clientName={clientName}
        locale={locale}
        onChanged={() => profile.refetch()}
      />

      {rateOpen && wheel ? (
        <RateWheelDrawer
          open
          clientId={clientId}
          clientName={clientName}
          wheel={wheel}
          locale={locale}
          lastRated={wheelRatedLine}
          onClose={() => setRateOpen(false)}
          onSaved={() => {
            wheelQuery.refetch();
            profile.refetch();
          }}
        />
      ) : null}
    </ToolSection>
  );
};
